import {
  ArrowLeft,
  CheckCircle2,
  FileUp,
  Loader2,
  TriangleAlert,
  Upload,
  X,
} from "lucide-react";
import { useRef, useState } from "react";
import { useParams } from "react-router";
import { toast } from "sonner";

import { ButtonLink } from "@/components/button-link";
import { CompetitionNotice } from "@/components/competition-notice";
import { EmptyState, ErrorState, LoadingRows, Spinner } from "@/components/data-state";
import { GoalBadge } from "@/components/goal-badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAsync, usePolling } from "@/hooks/use-async";
import { useApiKey } from "@/hooks/use-auth";
import { isPending, isSettled } from "@/lib/api";
import type { Challenge, Submission } from "@/lib/api";
import { ApiError, publicApi } from "@/lib/api";
import { legacyApi } from "@/legacy/api";
import {
  formatBytes,
  formatError,
  formatSeconds,
  formatSteps,
  isDeadlinePassed,
} from "@/lib/format";
import { cn } from "@/lib/utils";

/** Matches the server's MAX_UPLOAD_BYTES. */
const MAX_BYTES = 128 * 1024 * 1024;

function Dropzone({
  file,
  onSelect,
  disabled,
}: {
  file: File | null;
  onSelect: (file: File | null) => void;
  disabled?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  function accept(candidate: File | undefined) {
    if (!candidate) return;
    if (!candidate.name.endsWith(".npy")) {
      toast.error("Only .npy files are accepted");
      return;
    }
    if (candidate.size > MAX_BYTES) {
      toast.error(`File is too large (limit ${formatBytes(MAX_BYTES)})`);
      return;
    }
    onSelect(candidate);
  }

  if (file) {
    return (
      <div className="bg-muted/40 flex items-center gap-3 rounded-lg border p-4">
        <span
          className="bg-background grid size-9 shrink-0 place-items-center rounded-md border"
          aria-hidden
        >
          <FileUp className="text-muted-foreground size-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{file.name}</p>
          <p className="text-muted-foreground text-xs">{formatBytes(file.size)}</p>
        </div>
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={() => onSelect(null)}
          disabled={disabled}
          aria-label="Remove file"
        >
          <X className="size-4" aria-hidden />
        </Button>
      </div>
    );
  }

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        accept(e.dataTransfer.files[0]);
      }}
      className={cn(
        "flex w-full flex-col items-center gap-2 rounded-lg border border-dashed px-6 py-10 transition-colors",
        dragging ? "border-primary bg-primary/5" : "hover:bg-muted/50",
        disabled && "pointer-events-none opacity-50",
      )}
    >
      <span
        className="bg-muted grid size-10 place-items-center rounded-full"
        aria-hidden
      >
        <Upload className="text-muted-foreground size-5" />
      </span>
      <span className="text-sm font-medium">
        Click or drag to upload a .npy file
      </span>
      <span className="text-muted-foreground text-xs">
        from <code className="font-mono">data/save/</code>
      </span>
      <input
        ref={inputRef}
        type="file"
        accept=".npy"
        className="hidden"
        onChange={(e) => accept(e.target.files?.[0])}
      />
    </button>
  );
}

function Metric({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-muted-foreground text-xs">{label}</dt>
      <dd className="mt-1 text-sm font-medium tabular-nums">{value}</dd>
    </div>
  );
}

function Result({
  job,
  challenge,
}: {
  job: Submission;
  challenge: Challenge;
}) {
  if (isPending(job.status)) {
    return (
      <Card>
        <CardContent className="flex items-center gap-3 pt-6">
          <Spinner />
          <p className="text-sm font-medium">Scoring…</p>
        </CardContent>
      </Card>
    );
  }

  if (job.status === "failed") {
    return (
      <Card className="border-destructive/40 bg-destructive/5">
        <CardHeader>
          <CardTitle className="text-destructive flex items-center gap-2 text-base">
            <TriangleAlert className="size-4" aria-hidden />
            Evaluation failed
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <p className="font-mono text-xs break-words">{job.error_msg}</p>
          {job.error_msg?.includes("Maze mismatch") ? (
            <p className="text-muted-foreground">
              Wrong maze. Update <code className="font-mono">startup.json</code>,
              delete <code className="font-mono">data/maze.json</code>, and record
              again.
            </p>
          ) : null}
          {job.error_msg?.toLowerCase().includes("navigation") ? (
            <p className="text-muted-foreground">
              No navigation phase in the recording.
            </p>
          ) : null}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card className="border-emerald-600/30 bg-emerald-500/5">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base text-emerald-700 dark:text-emerald-300">
            <CheckCircle2 className="size-4" aria-hidden />
            Scored
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Metric
              label="Goal"
              value={
                job.score !== null ? (
                  <GoalBadge
                    tier={job.goal_tier}
                  />
                ) : (
                  "—"
                )
              }
            />
            <Metric
              label="Nav steps"
              value={job.nav_steps === null ? "—" : formatSteps(job.nav_steps)}
            />
            <Metric
              label="Distance"
              value={
                job.arrival === null ? "—" : formatError(job.arrival.distance_m)
              }
            />
            <Metric
              label="Time"
              value={
                job.nav_time_s === null ? "—" : formatSeconds(job.nav_time_s)
              }
            />
          </dl>
          <div className="flex flex-wrap gap-2">
            <ButtonLink
              size="sm"
              variant="outline"
              to={`/challenges/${challenge.id}/submissions/${job.job_id}`}
            >
              View details
            </ButtonLink>
            <ButtonLink size="sm" variant="ghost" to={`/challenges/${challenge.id}`}>
              Leaderboard
            </ButtonLink>
          </div>
        </CardContent>
      </Card>

      {/* The moment a student is most likely to stop, so the reminder is loudest
          here rather than only on the challenge page. */}
      <CompetitionNotice challenge={challenge} variant="next-step" />
    </div>
  );
}

export function ChallengeSubmitRoute() {
  const { id = "" } = useParams();
  const apiKey = useApiKey();

  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);

  // apiKey in the deps because an `enrolled` challenge only appears once signed in, and
  // signing in while on this page must refetch rather than leave it "not found".
  const challenges = useAsync(() => publicApi.challenges(), [apiKey]);
  const challenge = challenges.data?.find((c) => c.id === id);

  const job = usePolling<Submission>(
    jobId && apiKey ? () => legacyApi.status(jobId, apiKey) : null,
    (value) => isSettled(value.status),
    1500,
    [jobId, apiKey],
  );

  async function upload() {
    if (!file || !apiKey) return;
    setUploading(true);
    try {
      const { job_id } = await legacyApi.submit(id, file, apiKey);
      setJobId(job_id);
      setFile(null);
      toast.success("Uploaded — scoring now");
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  if (!apiKey) {
    return (
      <EmptyState
        title="Sign in to submit"
        action={
          <ButtonLink variant="outline" to={`/challenges/${id}`}>
            Back to challenge
          </ButtonLink>
        }
      />
    );
  }

  if (challenges.error) {
    return <ErrorState error={challenges.error} onRetry={challenges.refetch} />;
  }
  if (!challenges.data) return <LoadingRows rows={3} />;
  if (!challenge) {
    return (
      <EmptyState
        title="Challenge not available"
        action={
          <ButtonLink variant="outline" to="/challenges">
            Back to challenges
          </ButtonLink>
        }
      />
    );
  }

  const closed = isDeadlinePassed(challenge.deadline) || !challenge.active;

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <div>
        <ButtonLink
          variant="ghost"
          size="sm"
          to={`/challenges/${id}`}
          className="-ml-2.5"
        >
          <ArrowLeft className="size-4" aria-hidden />
          {challenge.name}
        </ButtonLink>
        <h1 className="mt-2 text-2xl font-semibold tracking-tight">
          Submit a run
        </h1>
      </div>

      {closed ? (
        <EmptyState title="This challenge is closed" />
      ) : (
        <>
          <CompetitionNotice challenge={challenge} />

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Recording</CardTitle>
              <CardDescription>
                Your best run counts — submit as often as you like.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Dropzone file={file} onSelect={setFile} disabled={uploading} />
              <Button
                onClick={upload}
                disabled={!file || uploading}
                className="w-full"
              >
                {uploading ? (
                  <>
                    <Loader2 className="size-4 animate-spin" aria-hidden />
                    Uploading
                  </>
                ) : (
                  <>
                    <FileUp className="size-4" aria-hidden />
                    Upload and score
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {job.error ? (
            <ErrorState error={job.error} onRetry={job.refetch} />
          ) : job.data ? (
            <Result job={job.data} challenge={challenge} />
          ) : null}
        </>
      )}
    </div>
  );
}
